import React, { useEffect, useState } from 'react';
import { Loader2, NotebookPen } from 'lucide-react';
import { toast } from 'sonner';
import api from '../api';
import { VitalsTrend } from './VitalsTrend';

const VITALS = [['bp', 'BP', 'e.g. 120/80'], ['pulse', 'Pulse', 'e.g. 72'], ['temp', 'Temp', 'e.g. 98.6'], ['o2', 'O2 %', 'e.g. 97'], ['pain', 'Pain 0-10', '0 to 10']];
const us = (iso = '') => (/^\d{4}-\d{2}-\d{2}$/.test(iso) ? `${iso.slice(5, 7)}/${iso.slice(8, 10)}/${iso.slice(0, 4)}` : iso);

export const VisitNoteList = ({ notes }) => (
  <ul className="space-y-2" data-testid="visit-note-list">
    {notes.map(n => (
      <li key={n.id} className="border rounded-lg p-3 bg-gray-50" data-testid={`visit-note-${n.id}`}>
        <div className="flex flex-wrap justify-between gap-2 text-xs text-gray-500">
          <span><strong className="text-gray-800">{n.authorName}</strong>, {n.visitType} visit {us(n.visitDate)}</span>
          <span>{new Date(n.createdAt).toLocaleString()}</span>
        </div>
        {Object.keys(n.vitals || {}).length > 0 && (
          <div className="flex flex-wrap gap-2 mt-1">
            {VITALS.filter(([k]) => n.vitals[k]).map(([k, l]) => (
              <span key={k} className="text-xs px-2 py-0.5 rounded-full bg-white border">{l}: <strong>{n.vitals[k]}</strong></span>
            ))}
          </div>
        )}
        <p className="text-sm text-gray-800 mt-1 whitespace-pre-wrap">{n.note}</p>
      </li>
    ))}
  </ul>
);

export const VisitNoteForm = ({ appointmentId, onSaved }) => {
  const [form, setForm] = useState({ note: '', bp: '', pulse: '', temp: '', o2: '', pain: '' });
  const [notes, setNotes] = useState([]);
  const [saving, setSaving] = useState(false);
  const load = () => api.get(`/visits/${appointmentId}/notes`).then(r => setNotes(r.data.notes || [])).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [appointmentId]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.post(`/visits/${appointmentId}/notes`, form);
      toast.success(r.data.message);
      setForm({ note: '', bp: '', pulse: '', temp: '', o2: '', pain: '' });
      load();
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save the note');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-3 border-t pt-3 space-y-2" data-testid={`visit-note-form-${appointmentId}`}>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        {VITALS.map(([k, l, ph]) => (
          <label key={k} className="text-xs text-gray-600">{l}
            <input value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} placeholder={ph}
              className="w-full mt-0.5 px-2 py-1.5 border rounded text-sm placeholder:text-gray-300" data-testid={`visit-note-${k}`} />
          </label>
        ))}
      </div>
      <textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={3}
        placeholder="Short visit note: what you did, how the patient is doing, follow-up needed"
        className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="visit-note-text" />
      <button onClick={save} disabled={saving || !form.note.trim()} data-testid="visit-note-save"
        className="px-4 py-1.5 rounded-full bg-[#d4af37] text-gray-900 text-sm font-semibold disabled:opacity-40 inline-flex items-center gap-2">
        {saving && <Loader2 className="w-4 h-4 animate-spin" />} Save Visit Note
      </button>
      {notes.length > 0 && <VisitNoteList notes={notes} />}
    </div>
  );
};

export const PatientVisitNotes = ({ patientId }) => {
  const [notes, setNotes] = useState(null);
  useEffect(() => {
    api.get(`/patients/${patientId}/visit-notes`).then(r => setNotes(r.data.notes || [])).catch(() => setNotes([]));
  }, [patientId]);
  return (
    <div className="bg-white rounded border p-4" data-testid="patient-visit-notes">
      <h3 className="font-semibold text-gray-800 flex items-center gap-2 mb-3"><NotebookPen className="w-4 h-4" /> Visit Notes</h3>
      {notes === null ? <Loader2 className="w-5 h-5 animate-spin" /> : notes.length === 0
        ? <p className="text-sm text-gray-500" data-testid="patient-visit-notes-empty">No visit notes yet.</p>
        : <><VitalsTrend notes={notes} /><VisitNoteList notes={notes} /></>}
    </div>
  );
};
